import { parsePhoneNumber } from 'libphonenumber-js'
import type { CountryCode } from 'libphonenumber-js'

export const COUNTRY_CODES = [
  { code: 'CA', label: 'Canada (+1)',           dial: '+1'   },
  { code: 'US', label: 'United States (+1)',    dial: '+1'   },
  { code: 'GB', label: 'United Kingdom (+44)', dial: '+44'  },
  { code: 'IN', label: 'India (+91)',           dial: '+91'  },
  { code: 'AU', label: 'Australia (+61)',       dial: '+61'  },
  { code: 'PK', label: 'Pakistan (+92)',        dial: '+92'  },
  { code: 'NG', label: 'Nigeria (+234)',        dial: '+234' },
  { code: 'PH', label: 'Philippines (+63)',     dial: '+63'  },
  { code: 'CN', label: 'China (+86)',           dial: '+86'  },
  { code: 'FR', label: 'France (+33)',          dial: '+33'  },
  { code: 'DE', label: 'Germany (+49)',         dial: '+49'  },
] as const

export type SupportedCountryCode = (typeof COUNTRY_CODES)[number]['code']

/**
 * Parse a national (local) phone number into E.164, given a country code.
 * Returns the E.164 string on success, null if the number is invalid.
 */
export function parseToE164(national: string, countryCode: SupportedCountryCode): string | null {
  const raw = national.trim()
  if (!raw) return null
  try {
    const parsed = parsePhoneNumber(raw, countryCode as CountryCode)
    if (parsed.isValid()) return parsed.number as string
  } catch {
    // invalid input
  }
  return null
}

/**
 * Split an E.164 phone number back into { countryCode, national }.
 * countryCode will be one of our COUNTRY_CODES entries, defaulting to 'CA' if unknown.
 * national is formatted in the local national format (e.g. "416 555 1234").
 * Returns null if the input cannot be parsed as a valid number.
 */
export function splitE164(e164: string): { countryCode: SupportedCountryCode; national: string } | null {
  if (!e164) return null
  try {
    const parsed = parsePhoneNumber(e164)
    if (!parsed.isValid()) return null
    const country = parsed.country ?? 'CA'
    const supported = COUNTRY_CODES.find(c => c.code === country)
    return {
      countryCode: (supported ? country : 'CA') as SupportedCountryCode,
      national: parsed.formatNational(),
    }
  } catch {
    return null
  }
}
